/// <amd-module name="Wit/SprintsHub/SprintsView/Components/SprintHeader/SprintDates" />

import { getNumberOfWorkingDays, getNumberWorkingDaysLeft, isTodayInSprint } from "Agile/Common/Utils/CapacityDateUtils";
import { IIterationData, ITeamSettings } from "Agile/Contracts/Common";
import * as React from "react";
import { TeamContext } from "Tfs/Platform/Contracts/Core";
import { localeDateFormat } from "VSS/Platform/Date";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, IVssLayoutManager } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Observer } from "VSSUI/Observer";
import { WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { DateRange } from "Wit/Clients/Work/Contracts/Work";
import { getWorkClient } from "Wit/Clients/Work/RestClient/Work";
import { shiftToUTC } from "Wit/Common/DateUtils";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { ITaskboardService } from "Wit/SprintsHub/SprintsView/Services/ITaskboardService";
import { SprintEditDatesDialog } from "./SprintEditDatesDialog";

const dateFormatOptions: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric"
};

export interface ISprintDatesProps {
    selectedIteration: ObservableValue<IIterationData | undefined>;
    teamSettings: ITeamSettings;
    onSprintDetailsChanged: (node: WorkItemClassificationNode) => void;
}

export class SprintDates extends TfsComponent<ISprintDatesProps, {}> {
    private hasBacklogPermission: IObservableValue<boolean> = new ObservableValue(false);
    private layoutManager: IVssLayoutManager;
    private teamDaysOff: IObservableValue<DateRange[]> = new ObservableValue([]);

    constructor(props: ISprintDatesProps, context: IVssComponentContext) {
        super(props, context);
        this.layoutManager = context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        props.teamSettings && this.loadSelectedIterationData();
        const taskboardService = this.context.pageContext.getService<ITaskboardService>("ITaskboardService");
        this.hasBacklogPermission.value = taskboardService.hasAgileToolsBacklogManagementPermission();
    }

    public componentDidUpdate() {
        this.props.teamSettings && this.loadSelectedIterationData();
    }

    public render(): JSX.Element | null {
        return (
            <Observer
                hasBacklogPermission={this.hasBacklogPermission}
                selectedIteration={this.props.selectedIteration}
                teamDaysOff={this.teamDaysOff}
            >
                {(observerProps: { hasBacklogPermission: boolean; selectedIteration: IIterationData | undefined; teamDaysOff: DateRange[] }) => {
                    const iterationDatesSelected =
                        observerProps.selectedIteration && observerProps.selectedIteration.startDate && observerProps.selectedIteration.finishDate;
                    return (
                        <div className="flex-column" role="menuitem">
                            <Button
                                className="sprint-dates-button"
                                disabled={!observerProps.hasBacklogPermission}
                                text={
                                    iterationDatesSelected
                                        ? this.getSprintDatesDisplay(
                                              shiftToUTC(observerProps.selectedIteration!.startDate!),
                                              shiftToUTC(observerProps.selectedIteration!.finishDate!)
                                          )
                                        : Resources.NoIterationDates
                                }
                                tooltipProps={{ text: observerProps.hasBacklogPermission ? Resources.EditIterationDatesTooltip : "" }}
                                onClick={this.onSprintDatesClick}
                            />
                            <span className="text-right">
                                {iterationDatesSelected && this.props.teamSettings.weekends?.days && observerProps.teamDaysOff
                                    ? this.getWorkingDaysMessage(
                                          shiftToUTC(observerProps.selectedIteration!.startDate!),
                                          shiftToUTC(observerProps.selectedIteration!.finishDate!),
                                          this.props.teamSettings.weekends?.days,
                                          observerProps.teamDaysOff
                                      )
                                    : observerProps.hasBacklogPermission && (
                                          <Button
                                              className="sprint-dates-button"
                                              text={Resources.SetDates}
                                              tooltipProps={{ text: Resources.SetDatesTooltip }}
                                              onClick={this.onSprintDatesClick}
                                          />
                                      )}
                            </span>
                        </div>
                    );
                }}
            </Observer>
        );
    }

    private getSprintDatesDisplay = (startDate: Date, finishDate: Date): string => {
        let display = "";
        if (startDate && finishDate) {
            display = format(
                Resources.SprintDateFormat,
                localeDateFormat(startDate, "M", new Intl.DateTimeFormat(undefined, dateFormatOptions)),
                localeDateFormat(finishDate, "M", new Intl.DateTimeFormat(undefined, dateFormatOptions)),
            );
        }
        return display;
    };

    private onSprintDatesClick = () => {
        this.openDialog(onDismiss => {
            const dismiss = () => {
                onDismiss();
            };
            return (
                <SprintEditDatesDialog
                    endDate={this.props.selectedIteration.value?.finishDate}
                    iterationGuid={this.props.selectedIteration.value?.id}
                    iterationId={this.props.selectedIteration.value?.nodeId}
                    iterationName={this.props.selectedIteration.value?.name}
                    iterationPath={this.props.selectedIteration.value?.friendlyPath}
                    startDate={this.props.selectedIteration.value?.startDate}
                    teamName={this.props.teamSettings.teamFieldDefaultValue}
                    onDismiss={dismiss}
                    onSprintDetailsChanged={this.props.onSprintDetailsChanged}
                />
            );
        });
    };

    private getWorkingDaysMessage(startDate: Date, endDate: Date, weekends: number[], teamDaysOff: DateRange[]): string {
        let workDays = getNumberOfWorkingDays(startDate, endDate, weekends, teamDaysOff);
        let message = "";
        if (isTodayInSprint(startDate, endDate)) {
            workDays = getNumberWorkingDaysLeft(endDate, weekends, teamDaysOff);
            message = workDays === 1 ? Resources.WorkingDayRemainingFormat : Resources.WorkingDaysRemainingFormat;
        } else {
            message = workDays === 1 ? Resources.WorkingDayFormat : Resources.WorkingDaysFormat;
        }
        return format(message, workDays);
    }

    private openDialog(callout: (onDismiss: () => void) => JSX.Element): void {
        this.layoutManager.renderCallout(onDismiss => callout(onDismiss));
    }

    private async loadSelectedIterationData() {
        if (this.props.teamSettings && this.props.teamSettings.teamId && this.props.teamSettings.projectId) {
            const teamContext: TeamContext = {
                teamId: this.props.teamSettings?.teamId,
                team: "",
                projectId: this.props.teamSettings.projectId,
                project: ""
            };
            const workRestClient = getWorkClient(this.context.pageContext);

            if (this.props.selectedIteration.value) {
                const teamDaysOff = (await workRestClient.getTeamDaysOff(teamContext, this.props.selectedIteration.value.id)).daysOff;
                if (teamDaysOff) {
                    this.teamDaysOff.value = teamDaysOff.map(function (dayOff) {
                        return { start: shiftToUTC(dayOff.start), end: shiftToUTC(dayOff.end) };
                    });
                }
            }
        }
    }
}
